import { useState, useRef } from 'react';
import { adminApi } from '@/lib/api';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download, Info } from 'lucide-react';

export default function ImportDataPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const res = await adminApi.importData(file);
      setResult(res.data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'import');
    }
    setUploading(false);
  };

  const downloadTemplate = () => {
    const headers = 'establishment_name;period;ca_ht;nb_clients;jours_ouverture;cout_matieres;cout_personnel;stock_moyen;cout_energie;impots_taxes;commissions_plateformes';
    const example = 'Le Neptune;2026-02;89000;2340;26;28480;33820;9200;3560;1780;890';
    const csv = `${headers}\n${example}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BENCHORY_template_import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="import-data-page">
      <div>
        <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Import de données</h1>
        <p className="text-gray-400 mt-1 text-sm">Importez des données mensuelles en masse via CSV ou Excel</p>
      </div>

      {/* Instructions */}
      <div className="bg-[#1B2A4A]/[0.03] rounded-xl p-5 border border-[#1B2A4A]/5">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-[#B8860B] shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-[#1B2A4A] mb-2">Format attendu</h3>
            <p className="text-xs text-gray-600 leading-relaxed mb-2">
              Le fichier doit contenir les colonnes suivantes (séparateur point-virgule pour CSV) :
            </p>
            <code className="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-700 block mb-3">
              establishment_name ; period ; ca_ht ; nb_clients ; jours_ouverture ; cout_matieres ; cout_personnel ; stock_moyen ; cout_energie ; impots_taxes ; commissions_plateformes
            </code>
            <p className="text-xs text-gray-500">
              Le nom de l'établissement doit correspondre exactement à un établissement existant. La période au format YYYY-MM.
            </p>
          </div>
        </div>
      </div>

      {/* Upload zone */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={downloadTemplate}
            className="text-[#B8860B] border border-[#B8860B]/30 px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#B8860B]/5 transition-all flex items-center gap-2"
            data-testid="download-template-btn">
            <Download size={16} /> Télécharger le modèle CSV
          </button>
        </div>

        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${file ? 'border-[#B8860B] bg-[#B8860B]/5' : 'border-gray-200 hover:border-gray-300'}`}
          onClick={() => fileRef.current?.click()}
          style={{ cursor: 'pointer' }}
          data-testid="import-dropzone"
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileSpreadsheet size={24} className="text-[#B8860B]" />
              <div className="text-left">
                <p className="text-sm font-bold text-[#1B2A4A]">{file.name}</p>
                <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} Ko</p>
              </div>
            </div>
          ) : (
            <>
              <Upload size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 font-medium">Cliquez pour sélectionner un fichier</p>
              <p className="text-xs text-gray-400 mt-1">CSV (.csv) ou Excel (.xlsx)</p>
            </>
          )}
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }}
            data-testid="import-file-input" />
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="import-error">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button onClick={handleUpload} disabled={!file || uploading}
            className="bg-[#1B2A4A] text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
            data-testid="import-submit-btn">
            {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
            Importer
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="import-results">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle size={18} className="text-emerald-600" />
            <h2 className="font-bold text-[#1B2A4A] font-manrope">
              Résultat : {result.imported}/{result.total_rows} lignes importées
            </h2>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {result.results.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1">
                {r.status === 'imported' ? (
                  <CheckCircle size={12} className="text-emerald-500 shrink-0" />
                ) : (
                  <AlertCircle size={12} className="text-red-500 shrink-0" />
                )}
                <span className="font-medium text-[#1B2A4A]">{r.row}</span>
                {r.period && <span className="text-gray-400">{r.period}</span>}
                {r.reason && <span className="text-red-500">{r.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
